import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { setAuthToken } from 'actions/auth/AuthActions';
import { lookupCustomerThunk, saveCustomerThunk } from 'actions/customer/CustomerActions';
import { CustomerError, CustomerErrorType } from 'actions/customer/CustomerErrors';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AlertScreen, AlertVariant } from 'types/Alert';
import { Customer } from 'types/Customer';
import { phoneAlreadyTakenMessage } from 'utils/alertMessages';
import { trackLogin, trackSignUp } from 'utils/analytics';
import { navigateToStep } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START,
    ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS,
    ONLINE_BOOKING_CREATE_CUSTOMER_START,
    ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS,
    ONLINE_BOOKING_ERROR
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { OnlineBookingStep } from 'types/OnlineBooking';

export const createOnlineBookingCustomerThunk = (
    customerData: Customer
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: ONLINE_BOOKING_CREATE_CUSTOMER_START });

            const {
                marketplace: { data: marketplace }
            } = getState();

            let customer = customerData;

            const randomPassword = Math.random().toString(36).substring(2, 15);

            const lookupCustomer = await dispatch(
                lookupCustomerThunk(customerData.email, marketplace.id)
            );

            if (
                (lookupCustomer && !lookupCustomer.phone) ||
                (lookupCustomer &&
                    lookupCustomer.email === customerData.email &&
                    lookupCustomer.phone !== customerData.phone?.replace(/\D/g, ''))
            ) {
                dispatch({ type: ONLINE_BOOKING_ERROR });

                return dispatch(
                    addAlertThunk(
                        'This user appears to be already registered in our system. Please contact your business directly to update your information.',
                        AlertScreen.OnlineBookingSignup,
                        AlertVariant.Info
                    )
                );
            }

            customer = await dispatch(
                saveCustomerThunk(
                    {
                        ...customerData,
                        // Prevent pets from being saved, they should be saved in the next step
                        pets: []
                    },
                    'online-booking',
                    randomPassword
                )
            );

            dispatch(setAuthToken(String(customer.token)));

            dispatch(
                navigateToStep(
                    customer.pets.length > 0
                        ? OnlineBookingStep.PetList
                        : OnlineBookingStep.CreatePet
                )
            );

            dispatch({
                type: ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS,
                payload: {
                    customer: {
                        ...customer,
                        lead: customer.lead ?? true
                    }
                }
            });

            // Analytics
            trackSignUp(marketplace.slug);
        } catch (error) {
            dispatch({ type: ONLINE_BOOKING_ERROR });

            if (error instanceof CustomerError) {
                const message =
                    error.type === CustomerErrorType.PhoneAlreadyTaken
                        ? phoneAlreadyTakenMessage
                        : error.message;

                const alertVariant =
                    error.type === CustomerErrorType.PhoneAlreadyTaken
                        ? AlertVariant.Info
                        : AlertVariant.Default;

                dispatch(addAlertThunk(message, AlertScreen.OnlineBookingSignup, alertVariant));
            }
        }
    };
};

export const authenticateOnlineBookingCustomerThunk = (
    token: string,
    customer: Customer,
    // The Add-Ons mode signs in without entering the booking wizard.
    { startWizard = true }: { startWizard?: boolean } = {}
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch({
            type: ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START
        });

        dispatch(setAuthToken(token));

        dispatch({
            type: ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS,
            payload: {
                customer: {
                    ...customer,
                    lead: customer.lead ?? true
                }
            }
        });

        if (!startWizard) {
            return;
        }

        // Analytics
        trackLogin(getState().marketplace.data.slug);

        dispatch(navigateToStep(OnlineBookingStep.PetList));
    };
};
